import AxiosConfig from '../utils/AxiosConfig';

class EmailListService {
  // Obtener todas las listas (sistema y personalizadas)
  async getLists(page = 1, pageSize = 10, searchTerm = '') {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString()
      });
      
      if (searchTerm && typeof searchTerm === 'string' && searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }
      
      const response = await AxiosConfig.get(`/marketing/email-lists?${params}`);
      
      // El backend devuelve: response.data.data.data (array de listas)
      // y response.data.data.total, page, pageSize para paginación
      const responseData = response.data?.data?.data || response.data?.data || response.data;
      const paginationData = response.data?.data || {};
      
      // Retornar en formato consistente: { data: [...], total, page, pageSize }
      return {
        data: Array.isArray(responseData) ? responseData : [],
        total: paginationData.total || (Array.isArray(responseData) ? responseData.length : 0),
        page: paginationData.page || page,
        pageSize: paginationData.pageSize || pageSize
      };
    } catch (error) {
      console.error('[EmailListService] GET /marketing/email-lists - Error:', error);
      console.error('[EmailListService] GET /marketing/email-lists - Error Response:', error.response?.data);
      throw error;
    }
  }

  // Obtener una lista específica por ID (incluye contacts)
  async getListById(listId) {
    try { 
      const response = await AxiosConfig.get(`/marketing/email-lists/${listId}`);
     
      // El backend devuelve { list: {...}, contacts: [...] }
      const responseData = response.data?.data || response.data;
      const listData = responseData?.list || responseData;
      const contacts = responseData?.contacts || [];
      
      // Retornar tanto la lista como los contacts
      return {
        ...listData,
        contacts: contacts
      };
    } catch (error) {
      console.error('[EmailListService] GET /marketing/email-lists/' + listId + ' - Error:', error);
      console.error('[EmailListService] GET /marketing/email-lists/' + listId + ' - Error Response:', error.response?.data);
      throw error;
    }
  }

  // Crear una nueva lista
  async createList(listData) {
    try {
      console.log('[EmailListService] POST /marketing/email-lists - Request:', {
        name: listData.name,
        description: listData.description || '',
        emailsCount: listData.emails?.length || 0,
        emails: listData.emails || []
      });
      
      const response = await AxiosConfig.post('/marketing/email-lists', listData);
      
      // El backend puede devolver response.data.data o response.data directamente
      const responseData = response.data?.data || response.data;
      
      console.log('[EmailListService] POST /marketing/email-lists - Response:', {
        id: responseData?.id,
        name: responseData?.name,
        isSystem: responseData?.isSystem,
        contacts: responseData?.contacts
      });
      
      return responseData;
    } catch (error) {
      console.error('[EmailListService] POST /marketing/email-lists - Error:', error);
      console.error('[EmailListService] POST /marketing/email-lists - Error Response:', error.response?.data);
      throw error;
    }
  }

  // Actualizar una lista
  async updateList(listId, listData) {
    try {
      // Asegurarse de que solo se envíen los campos necesarios
      const requestData = {
        name: listData.name,
        description: listData.description || '',
        emails: listData.emails || []
      };
      
      console.log('[EmailListService] PUT /marketing/email-lists/' + listId + ' - Request URL:', `/marketing/email-lists/${listId}`);
      console.log('[EmailListService] PUT /marketing/email-lists/' + listId + ' - Request Body:', requestData);
      
      const response = await AxiosConfig.put(`/marketing/email-lists/${listId}`, requestData);
      
      // El backend puede devolver response.data.data o response.data directamente
      const responseData = response.data?.data || response.data;
      
      console.log('[EmailListService] PUT /marketing/email-lists/' + listId + ' - Response:', {
        id: responseData?.id,
        name: responseData?.name,
        contacts: responseData?.contacts
      });
      
      return responseData;
    } catch (error) {
      console.error('[EmailListService] PUT /marketing/email-lists/' + listId + ' - Error:', error);
      console.error('[EmailListService] PUT /marketing/email-lists/' + listId + ' - Error Response:', error.response?.data);
      throw error;
    }
  }

  // Eliminar una lista
  async deleteList(listId) {
    try {
      console.log('[EmailListService] DELETE /marketing/email-lists/' + listId + ' - Request');
      
      const response = await AxiosConfig.delete(`/marketing/email-lists/${listId}`);
      
      // El backend puede devolver response.data.data o response.data directamente
      const deleteData = response.data?.data || response.data;
      
      console.log('[EmailListService] DELETE /marketing/email-lists/' + listId + ' - Response:', deleteData);
      
      return deleteData;
    } catch (error) {
      console.error('[EmailListService] DELETE /marketing/email-lists/' + listId + ' - Error:', error);
      console.error('[EmailListService] DELETE /marketing/email-lists/' + listId + ' - Error Response:', error.response?.data);
      throw error;
    }
  }

  // Obtener clientes para selección en listas (B2C y B2B unificado)
  async getClients(page = 1, pageSize = 10, search = '', type = 'all') {
    try {
      // Construir parámetros de la query
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('pageSize', pageSize.toString());
      
      if (search && typeof search === 'string' && search.trim()) {
        params.append('search', search.trim());
      }
      
      // type puede ser: 'all', 'b2b', 'b2c' (siempre en minúsculas)
      const typeValue = type && type !== 'all' ? type.toLowerCase() : 'all';
      params.append('type', typeValue);
      
      const queryString = params.toString();
      const url = `/marketing/clients?${queryString}`;
      
      console.log('[EmailListService] GET /marketing/clients - Request:', {
        url: url,
        queryString: queryString,
        page: page,
        pageSize: pageSize,
        search: search || '(vacío)',
        type: typeValue,
        paramsObject: Object.fromEntries(params)
      });
      
      const response = await AxiosConfig.get(url);
      
      // El backend debería devolver { clients: [...], total: number } o { data: { clients: [...], total: number } }
      const responseData = response?.data?.data || response?.data || response;
      const clientsData = responseData?.clients || responseData?.data || responseData || [];
      const total = responseData?.total || responseData?.totalCount || clientsData.length || 0;
      
      console.log('[EmailListService] GET /marketing/clients - Response:', {
        clientsCount: Array.isArray(clientsData) ? clientsData.length : 0,
        total: total,
        page: page,
        pageSize: pageSize,
        hasSearch: !!search,
        type: type || 'all'
      });
      
      return {
        clients: Array.isArray(clientsData) ? clientsData : [],
        total: total,
        page: page,
        pageSize: pageSize
      };
    } catch (error) {
      console.error('[EmailListService] GET /marketing/clients - Error:', error);
      console.error('[EmailListService] GET /marketing/clients - Error Response:', error.response?.data);
      throw error;
    }
  }

}

export default new EmailListService();

